import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  BookOpen, 
  ArrowRight, 
  Clock, 
  BarChart3, 
  Star,
  TrendingUp,
  Eye,
  Target,
  RefreshCw,
  Filter,
  CheckCircle,
  Lock,
  AlertCircle,
  Brain,
  Zap,
  Users,
  Lightbulb,
  Timer,
  User
} from 'lucide-react';
import { useAdvancedRecommendations } from '@/hooks/useAdvancedRecommendations';
import { useRecommendations, useRefreshRecommendations, useTrackRecommendation } from '@/hooks/useRecommendations';
import { useAdvancedFilters } from '@/hooks/useAdvancedFilters';
import { useRealtimeRecommendations } from '@/hooks/useRealtimeRecommendations';
import { useContentFreshness } from '@/hooks/useContentFreshness';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

export const RecommendedTab: React.FC = () => {
  const { user } = useAuth();
  const [showFilters, setShowFilters] = useState(false);
  const [difficultyFilter, setDifficultyFilter] = useState<number | null>(null);
  const [recommendationLimit, setRecommendationLimit] = useState(8);
  const [timeFilter, setTimeFilter] = useState<number | null>(null);
  const [useAdvancedAlgorithm, setUseAdvancedAlgorithm] = useState(true);

  // Advanced recommendations context
  const recommendationContext = {
    timeAvailable: timeFilter || undefined,
    limit: recommendationLimit,
    learningGoal: 'exploration' as const,
    sessionType: 'mixed' as const
  };

  // Hooks - use advanced or basic recommendations
  const { 
    data: advancedRecommendations = [], 
    isLoading: isAdvancedLoading, 
    error: advancedError 
  } = useAdvancedRecommendations(useAdvancedAlgorithm ? recommendationContext : undefined);
  
  const { 
    data: basicRecommendations = [], 
    isLoading: isBasicLoading, 
    error: basicError, 
    refetch 
  } = useRecommendations(recommendationLimit);
  
  const refreshRecommendations = useRefreshRecommendations();
  const trackRecommendation = useTrackRecommendation();

  // Choose which recommendations to use
  const recommendations = useAdvancedAlgorithm ? advancedRecommendations : basicRecommendations;
  const isLoading = useAdvancedAlgorithm ? isAdvancedLoading : isBasicLoading;
  const error = useAdvancedAlgorithm ? advancedError : basicError;

  // Get user stats for the header
  const { data: userStats } = useQuery({
    queryKey: ['user-recommendation-stats', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data } = await supabase
        .from('user_progress')
        .select('status')
        .eq('user_id', user.id);
      
      const completed = data?.filter(p => p.status === 'completed').length || 0;
      const inProgress = data?.filter(p => p.status === 'in_progress').length || 0;
      
      return { completed, inProgress, total: data?.length || 0 };
    },
    enabled: !!user?.id
  });

  // Filter recommendations
  const filteredRecommendations = recommendations.filter(rec => {
    if (difficultyFilter !== null && rec.difficulty_level !== difficultyFilter) {
      return false;
    }
    if (timeFilter !== null && rec.estimated_study_time > timeFilter) {
      return false;
    }
    return true;
  });

  // Handle recommendation click
  const handleRecommendationClick = (nodeId: string, title: string) => {
    if (user?.id) {
      trackRecommendation.mutate({
        nodeId,
        action: 'clicked',
        userId: user.id
      });
    }
    
    console.log('Navigating to node:', nodeId, title);
  };

  // Handle refresh
  const handleRefresh = () => {
    refreshRecommendations.mutate();
  };

  // Get confidence score color
  const getConfidenceColor = (score: number) => {
    if (score >= 85) return 'text-green-600 bg-white';
    if (score >= 70) return 'text-black bg-white';
    if (score >= 55) return 'text-black bg-white';
    return 'text-black bg-white';
  };

  // Get freshness badge
  const getFreshnessBadge = (freshness: string) => {
    switch (freshness) {
      case 'new':
        return <Badge className="bg-white text-green-700 border-green-200">New</Badge>;
      case 'trending':
        return <Badge className="bg-white text-black border-purple-200">Trending</Badge>;
      case 'updated':
        return <Badge className="bg-white text-black border-blue-200">Updated</Badge>;
      default:
        return null;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-80" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="h-80">
              <CardHeader>
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-8 w-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <CardTitle className="text-red-700">Unable to Load Recommendations</CardTitle>
          </div>
          <CardDescription className="text-red-600">
            We're having trouble getting your personalized recommendations. Please try again.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetch()} variant="outline" className="border-red-300 text-red-700">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-black flex items-center gap-2">
            <Brain className="h-6 w-6 text-black" />
            {useAdvancedAlgorithm ? 'AI-Powered Recommendations' : 'Smart Recommendations'}
          </h2>
          <p className="text-black mt-1">
            {useAdvancedAlgorithm 
              ? 'Multi-factor personalized learning paths with confidence scoring'
              : 'Personalized learning paths based on your progress and interests'
            }
          </p>
          {userStats && (
            <div className="flex gap-4 mt-2 text-sm text-black">
              <span>{userStats.completed} completed</span>
              <span>{userStats.inProgress} in progress</span>
              <span>{userStats.total} total nodes accessed</span>
            </div>
          )}
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant={useAdvancedAlgorithm ? "default" : "outline"}
            size="sm"
            onClick={() => setUseAdvancedAlgorithm(!useAdvancedAlgorithm)}
          >
            <Brain className="h-4 w-4 mr-2" />
            {useAdvancedAlgorithm ? 'AI Mode' : 'Enable AI'}
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleRefresh}
            disabled={refreshRecommendations.isPending}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshRecommendations.isPending ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card className="bg-white">
          <CardContent className="pt-6">
            <div className="space-y-4">
              {/* Difficulty Filter */}
              <div className="flex gap-4 items-center">
                <label className="text-sm font-medium w-24">Difficulty:</label>
                <div className="flex gap-2">
                  <Button
                    variant={difficultyFilter === null ? "default" : "outline"}
                    size="sm"
                    onClick={() => setDifficultyFilter(null)}
                  >
                    All
                  </Button>
                  {[1, 2, 3, 4, 5].map(level => (
                    <Button
                      key={level}
                      variant={difficultyFilter === level ? "default" : "outline"}
                      size="sm"
                      onClick={() => setDifficultyFilter(level)}
                    >
                      {level}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Time Filter */}
              <div className="flex gap-4 items-center">
                <label className="text-sm font-medium w-24">Max Time:</label>
                <div className="flex gap-2">
                  <Button
                    variant={timeFilter === null ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTimeFilter(null)}
                  >
                    Any
                  </Button>
                  {[15, 30, 45, 60, 90].map(minutes => (
                    <Button
                      key={minutes}
                      variant={timeFilter === minutes ? "default" : "outline"}
                      size="sm"
                      onClick={() => setTimeFilter(minutes)}
                    >
                      {minutes}m
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Algorithm Features Info */}
      {useAdvancedAlgorithm && recommendations.length > 0 && (
        <Card className="bg-white from-purple-50 to-blue-50 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="h-5 w-5 text-black" />
              <h3 className="font-medium text-purple-900">AI Algorithm Features</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2 text-black">
                <User className="h-4 w-4" />
                Learning Style Match
              </div>
              <div className="flex items-center gap-2 text-black">
                <Target className="h-4 w-4" />
                Difficulty Optimization
              </div>
              <div className="flex items-center gap-2 text-black">
                <Timer className="h-4 w-4" />
                Time Optimization
              </div>
              <div className="flex items-center gap-2 text-black">
                <Users className="h-4 w-4" />
                Peer Similarity
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {filteredRecommendations.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-black mb-2">
              No Recommendations Available
            </h3>
            <p className="text-black mb-6">
              {timeFilter || difficultyFilter 
                ? 'Try adjusting your filters to see more recommendations.'
                : 'Complete a few more learning activities to get personalized recommendations.'
              }
            </p>
            <Button onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Recommendations
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Recommendations Grid */}
      {filteredRecommendations.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2">
          {filteredRecommendations.map((recommendation, index) => {
            const isAdvanced = 'algorithmFactors' in recommendation;
            const confidence = recommendation.confidenceScore || 0;
            
            return (
              <Card 
                key={recommendation.node_id} 
                className={`transition-all duration-200 hover:shadow-lg cursor-pointer border-l-4 ${
                  index === 0 ? 'border-l-purple-500 bg-white from-purple-50 to-white' :
                  confidence >= 85 ? 'border-l-green-500' :
                  confidence >= 70 ? 'border-l-blue-500' :
                  'border-l-gray-400'
                }`}
                onClick={() => handleRecommendationClick(recommendation.node_id, recommendation.title)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg font-semibold text-black line-clamp-2">
                        {recommendation.title}
                      </CardTitle>
                      <CardDescription className="mt-1 text-sm">
                        {recommendation.discipline_name || 'General Studies'}
                      </CardDescription>
                    </div>
                    <div className="flex flex-col gap-1 ml-2">
                      {index === 0 && (
                        <Badge variant="secondary" className="bg-white text-black">
                          <Star className="h-3 w-3 mr-1" />
                          Top Pick
                        </Badge>
                      )}
                      {isAdvanced && 'freshness' in recommendation && getFreshnessBadge(recommendation.freshness)}
                    </div>
                  </div>
                  
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">
                      {recommendation.recommendationReason || (recommendation as any).matchReason}
                    </Badge>
                    {isAdvanced && 'contextualTags' in recommendation && 
                      recommendation.contextualTags.slice(0, 2).map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))
                    }
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  {/* Metadata */}
                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div className="flex items-center gap-2 text-black">
                      <BarChart3 className="h-4 w-4" />
                      Level {recommendation.difficulty_level}
                    </div>
                    <div className="flex items-center gap-2 text-black">
                      <Clock className="h-4 w-4" />
                      ~{recommendation.estimated_study_time} min
                    </div>
                  </div>

                  {/* Algorithm Factors (Advanced Mode) */}
                  {isAdvanced && 'algorithmFactors' in recommendation && (
                    <div className="mb-4 p-3 bg-white rounded-lg">
                      <h4 className="text-sm font-medium text-black mb-2 flex items-center gap-1">
                        <Brain className="h-4 w-4" />
                        AI Analysis
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex justify-between">
                          <span>Learning Style:</span>
                          <span className="font-medium">{Math.round(recommendation.algorithmFactors.learningStyleMatch * 100)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Difficulty:</span>
                          <span className="font-medium">{Math.round(recommendation.algorithmFactors.difficultyProgression * 100)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Interest:</span>
                          <span className="font-medium">{Math.round(recommendation.algorithmFactors.interestAlignment * 100)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Time Match:</span>
                          <span className="font-medium">{Math.round(recommendation.algorithmFactors.timeOptimization * 100)}%</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Personalized Metadata (Advanced Mode) */}
                  {isAdvanced && 'personalizedMetadata' in recommendation && (
                    <div className="mb-4 p-3 bg-white rounded-lg">
                      <h4 className="text-sm font-medium text-black mb-2 flex items-center gap-1">
                        <Lightbulb className="h-4 w-4" />
                        Personalized Insights
                      </h4>
                      <div className="space-y-1 text-xs text-black">
                        <div>Difficulty: {recommendation.personalizedMetadata.difficultyMatch}</div>
                        <div>Engagement: {Math.round(recommendation.personalizedMetadata.estimatedEngagement * 100)}%</div>
                        <div>Study Time: {recommendation.personalizedMetadata.studyTimeEstimate} min estimated</div>
                      </div>
                    </div>
                  )}

                  {/* Prerequisites Status */}
                  <div className="mb-4">
                    {recommendation.prerequisite_status?.all_completed ? (
                      <div className="flex items-center gap-2 text-green-600 text-sm">
                        <CheckCircle className="h-4 w-4" />
                        Ready to start
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-black text-sm">
                        <Lock className="h-4 w-4" />
                        {recommendation.prerequisite_status?.completion_rate 
                          ? `${Math.round(recommendation.prerequisite_status.completion_rate * 100)}% prerequisites completed`
                          : 'Some prerequisites required'
                        }
                      </div>
                    )}
                  </div>

                  {/* Confidence Score */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs ${getConfidenceColor(confidence)}`}>
                        <TrendingUp className="h-3 w-3 mr-1" />
                        {confidence}% match
                      </Badge>
                      {isAdvanced && 'priorityScore' in recommendation && (
                        <Badge variant="outline" className="text-xs">
                          Priority: {Math.round(recommendation.priorityScore)}
                        </Badge>
                      )}
                    </div>
                    
                    <Button 
                      size="sm" 
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      Start Learning
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Load More */}
      {filteredRecommendations.length > 0 && recommendations.length >= recommendationLimit && (
        <div className="text-center">
          <Button 
            variant="outline" 
            onClick={() => setRecommendationLimit(prev => prev + 4)}
          >
            <Eye className="h-4 w-4 mr-2" />
            Show More Recommendations
          </Button>
        </div>
      )}
    </div>
  );
}; 